import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { UserRole } from '../../../../core/auth/models/auth.models';
import { ToastService } from '../../../../core/services/toast.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { PartnerOption } from '../../models/user.models';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-user-create',
  imports: [CommonModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './user-create.component.html',
  styleUrl: './user-create.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserCreateComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);
  private readonly toastService = inject(ToastService);

  readonly loadingPartners = signal(false);
  readonly creating = signal(false);
  readonly partners = signal<PartnerOption[]>([]);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', route: '/home' },
    { label: 'Users', route: '/users' },
    { label: 'Create User' },
  ];

  readonly form = this.fb.nonNullable.group({
    fullName: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    role: ['partner' as UserRole, [Validators.required]],
    partnerOrganizationId: [''],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/[A-Z]/),
        Validators.pattern(/[a-z]/),
        Validators.pattern(/[0-9]/),
      ],
    ],
    confirmPassword: ['', [Validators.required]],
  });

  readonly isPartnerRole = computed(() => this.form.controls.role.getRawValue() === 'partner');

  constructor() {
    this.form.addValidators((group) => {
      const password = group.get('password')?.value ?? '';
      const confirmPassword = group.get('confirmPassword')?.value ?? '';
      return password === confirmPassword ? null : { passwordMismatch: true };
    });

    this.form.controls.role.valueChanges.subscribe((role) => {
      if (role !== 'partner') {
        this.form.controls.partnerOrganizationId.clearValidators();
        this.form.controls.partnerOrganizationId.setValue('', { emitEvent: false });
        this.form.controls.partnerOrganizationId.updateValueAndValidity({ emitEvent: false });
        return;
      }

      this.form.controls.partnerOrganizationId.setValidators([Validators.required]);
      this.form.controls.partnerOrganizationId.updateValueAndValidity({ emitEvent: false });
    });

    this.form.controls.partnerOrganizationId.setValidators([Validators.required]);
    this.form.controls.partnerOrganizationId.updateValueAndValidity({ emitEvent: false });

    this.loadPartners();
  }

  goBackToUsers(): void {
    void this.router.navigate(['/users']);
  }

  submit(): void {
    if (this.form.invalid || this.creating()) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const role = raw.role as UserRole;
    const partnerOrganizationId =
      role === 'partner'
        ? this.toPartnerOrganizationIdOrNull(raw.partnerOrganizationId)
        : null;

    if (role === 'partner' && partnerOrganizationId == null) {
      this.toastService.warning('Partner Required', 'Select a partner organization for partner users.');
      return;
    }

    this.creating.set(true);

    this.usersService
      .createUser({
        fullName: raw.fullName.trim(),
        email: raw.email.trim().toLowerCase(),
        role,
        partnerOrganizationId,
        password: raw.password,
      })
      .pipe(finalize(() => this.creating.set(false)))
      .subscribe({
        next: ({ user }) => {
          this.toastService.success('User Created', 'New user created successfully.');
          void this.router.navigate(['/users', user.id, 'profile']);
        },
        error: (error) => {
          this.toastService.danger(
            'Create Failed',
            error?.error?.message ?? 'Unable to create user right now.',
          );
        },
      });
  }

  private loadPartners(): void {
    this.loadingPartners.set(true);

    this.usersService
      .listPartners()
      .pipe(finalize(() => this.loadingPartners.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.partners.set(data ?? []);
        },
        error: () => {
          this.partners.set([]);
        },
      });
  }

  private toPartnerOrganizationIdOrNull(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }
}
